import React, { useState, useEffect, useRef, useCallback } from "react";
import { API } from "@/App";
import { getRandomEncouragement } from "@/data/gameData";
import { ArrowLeft, Timer, Eye, RotateCcw, Check } from "lucide-react";
import axios from "axios";

export default function PracticeMode({ item, mode, onBack, onMemorized, userId }) {
  const [words, setWords] = useState([]);
  const [hiddenIndices, setHiddenIndices] = useState(new Set());
  const [revealedIndices, setRevealedIndices] = useState(new Set());
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerActive, setTimerActive] = useState(true);
  const [showExtendPrompt, setShowExtendPrompt] = useState(false);
  const timerRef = useRef(null);
  const fiveMinHit = useRef(false);

  // Parse content into words with line breaks
  useEffect(() => {
    if (!item?.content) return;
    const lines = item.content.split("\n");
    const allWords = [];
    lines.forEach((line, lineIdx) => {
      if (lineIdx > 0) allWords.push({ text: "\n", isBreak: true, idx: allWords.length });
      line.split(/\s+/).filter(Boolean).forEach(word => {
        allWords.push({ text: word, isBreak: false, idx: allWords.length });
      });
    });
    setWords(allWords);

    // Set initial hidden words based on mode
    const hidden = new Set();
    if (mode === "first_letters") {
      allWords.forEach((w, i) => {
        if (!w.isBreak && /[a-zA-Z]/.test(w.text)) hidden.add(i);
      });
    } else if (mode === "fill_blanks") {
      // User chooses which words to hide by clicking
      // Start with some random ones hidden
      const wordIndices = allWords.map((w, i) => (!w.isBreak && /[a-zA-Z]/.test(w.text)) ? i : -1).filter(i => i >= 0);
      const toHide = Math.max(3, Math.floor(wordIndices.length * 0.3));
      const shuffled = [...wordIndices].sort(() => Math.random() - 0.5);
      shuffled.slice(0, toHide).forEach(i => hidden.add(i));
    }
    setHiddenIndices(hidden);
    setRevealedIndices(new Set());
  }, [item, mode]);

  // Timer
  useEffect(() => {
    if (timerActive) {
      timerRef.current = setInterval(() => {
        setTimerSeconds(prev => {
          const next = prev + 1;
          if (next === 300 && !fiveMinHit.current) {
            fiveMinHit.current = true;
            setShowExtendPrompt(true);
            setTimerActive(false);
          }
          return next;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [timerActive]);

  const toggleWord = (idx) => {
    if (mode === "fill_blanks") {
      // Toggle hide/reveal
      const newHidden = new Set(hiddenIndices);
      const newRevealed = new Set(revealedIndices);
      if (hiddenIndices.has(idx) && !revealedIndices.has(idx)) {
        newRevealed.add(idx);
        setRevealedIndices(newRevealed);
      } else if (revealedIndices.has(idx)) {
        newRevealed.delete(idx);
        setRevealedIndices(newRevealed);
      } else {
        newHidden.add(idx);
        setHiddenIndices(newHidden);
      }
    } else if (mode === "first_letters") {
      const newRevealed = new Set(revealedIndices);
      if (revealedIndices.has(idx)) {
        newRevealed.delete(idx);
      } else {
        newRevealed.add(idx);
      }
      setRevealedIndices(newRevealed);
    }
  };

  const resetPractice = () => {
    setRevealedIndices(new Set());
    if (mode === "first_letters") {
      const hidden = new Set();
      words.forEach((w, i) => {
        if (!w.isBreak && /[a-zA-Z]/.test(w.text)) hidden.add(i);
      });
      setHiddenIndices(hidden);
    }
  };

  const handleGotIt = useCallback(async () => {
    // Record practice session
    if (userId) {
      try {
        await axios.post(`${API}/users/${userId}/practice-session`, {
          item_id: item?.id,
          duration_seconds: timerSeconds
        });
      } catch { /* silent */ }
    }
    // Only call onMemorized if item is not already memorized
    // For already-memorized items, just go back with encouragement
    if (item?.status === "memorized") {
      onBack();
    } else {
      onMemorized();
    }
  }, [userId, item, timerSeconds, onMemorized, onBack]);

  const extendTime = () => {
    setShowExtendPrompt(false);
    setTimerActive(true);
  };

  const getFirstLetter = (text) => {
    const match = text.match(/^([^a-zA-Z]*[a-zA-Z])/);
    if (match) {
      const rest = text.slice(match[0].length);
      const dots = rest.replace(/[a-zA-Z]/g, "\u2022").replace(/[^a-zA-Z\u2022]/g, c => c);
      return match[0] + dots;
    }
    return text;
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <div className="slide-up" data-testid="practice-mode">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => {
          if (userId && timerSeconds > 0) {
            axios.post(`${API}/users/${userId}/practice-session`, {
              item_id: item?.id, duration_seconds: timerSeconds
            }).catch(() => {});
          }
          onBack();
        }} className="flex items-center gap-2 font-heading font-semibold" style={{ color: "#7A5946" }}
          data-testid="practice-back-btn">
          <ArrowLeft className="w-5 h-5" /> Back
        </button>
        <div className="flex items-center gap-2 tt-card px-3 py-1" data-testid="practice-timer">
          <Timer className="w-4 h-4" style={{ color: "#D97706" }} />
          <span className="font-heading text-sm font-bold" style={{ color: "#D97706" }}>{formatTime(timerSeconds)}</span>
        </div>
      </div>

      <h2 className="font-heading text-lg font-bold mb-1" style={{ color: "#4A3224" }}>{item.title}</h2>
      {item.author && <p className="text-xs mb-1" style={{ color: "#7A5946" }}>by {item.author}</p>}
      <p className="text-xs mb-4" style={{ color: "#7A5946" }}>
        {mode === "first_letters" ? "Tap a word to reveal it" : "Tap words to hide/reveal them"}
      </p>

      {/* "I Got It" button above poem */}
      <button onClick={handleGotIt}
        className="tt-btn tt-btn-gold w-full py-3 text-base mb-4"
        data-testid="i-got-it-btn">
        <Check className="w-5 h-5 inline mr-2" /> I Got It!
      </button>

      {/* Word Cards */}
      <div className="parchment rounded-xl p-4 mb-4 min-h-[200px]" data-testid="practice-word-area">
        <div className="flex flex-wrap items-start gap-0">
          {words.map((word, idx) => {
            if (word.isBreak) return <div key={idx} className="w-full h-2" />;

            const isHidden = hiddenIndices.has(idx) && !revealedIndices.has(idx);
            const isRevealed = revealedIndices.has(idx);
            const isInteractive = hiddenIndices.has(idx) || mode === "fill_blanks";

            let displayText = word.text;
            if (mode === "first_letters" && isHidden) {
              displayText = getFirstLetter(word.text);
            } else if (mode === "fill_blanks" && isHidden) {
              displayText = "\u2582".repeat(Math.min(word.text.length, 6));
            }

            return (
              <button
                key={idx}
                onClick={() => isInteractive && toggleWord(idx)}
                className={`word-card ${isHidden ? "hidden-word" : ""} ${isRevealed ? "revealed-word" : ""}`}
                style={{ cursor: isInteractive ? "pointer" : "default" }}
                data-testid={`word-${idx}`}
              >
                {displayText}
              </button>
            );
          })}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button onClick={resetPractice} className="tt-btn flex-1 py-2 text-sm flex items-center justify-center gap-1"
          style={{ background: "white" }} data-testid="reset-practice-btn">
          <RotateCcw className="w-4 h-4" /> Reset
        </button>
        <button onClick={() => setRevealedIndices(new Set(hiddenIndices))}
          className="tt-btn flex-1 py-2 text-sm flex items-center justify-center gap-1"
          style={{ background: "white" }} data-testid="reveal-all-btn">
          <Eye className="w-4 h-4" /> Reveal All
        </button>
      </div>

      {/* 5-Minute Prompt */}
      {showExtendPrompt && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          data-testid="extend-prompt">
          <div className="tt-card p-6 max-w-sm text-center">
            <div className="text-4xl mb-3">🎉</div>
            <h3 className="font-heading text-xl font-bold mb-2" style={{ color: "#4A3224" }}>
              Great job!
            </h3>
            <p className="text-sm mb-4" style={{ color: "#7A5946" }}>
              You've spent 5 minutes practicing! {getRandomEncouragement()}
            </p>
            <p className="text-sm mb-4" style={{ color: "#7A5946" }}>Would you like more time?</p>
            <div className="flex gap-3">
              <button onClick={extendTime} className="tt-btn tt-btn-ocean flex-1 py-2 text-sm"
                data-testid="extend-time-btn">More Time!</button>
              <button onClick={() => { setShowExtendPrompt(false); onBack(); }}
                className="tt-btn flex-1 py-2 text-sm" style={{ background: "white" }}
                data-testid="done-practice-btn">I'm Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
